import type { QueryClient } from "@tanstack/react-query";
import { studyGroupsApi } from "@/lib/humhub-api/studyGroups";
import {
  finalizeMembershipJoin,
  type FinalizeMembershipJoinResult,
  type MembershipVisitSource,
} from "@/lib/study-group/finalizeMembershipJoin";

export type MembershipTransitionAction =
  | "join"
  | "accept_invitation"
  | "force_join";

interface CompleteMembershipTransitionParams {
  queryClient: QueryClient;
  studyGroupId: string | number;
  action: MembershipTransitionAction;
  source?: MembershipVisitSource;
}

const performMembershipTransition = async (
  action: MembershipTransitionAction,
  studyGroupId: string | number,
) => {
  switch (action) {
    case "join":
      return studyGroupsApi.join(studyGroupId);
    case "accept_invitation":
      return studyGroupsApi.acceptInvitation(studyGroupId);
    case "force_join":
      return studyGroupsApi.forceJoin(studyGroupId);
    default: {
      const exhaustiveCheck: never = action;
      throw new Error(
        `Unhandled membership transition action: ${exhaustiveCheck}`,
      );
    }
  }
};

const defaultSourceByAction: Record<
  MembershipTransitionAction,
  MembershipVisitSource
> = {
  join: "join",
  accept_invitation: "accept_invitation",
  force_join: "force_join",
};

export const completeMembershipTransition = async ({
  queryClient,
  studyGroupId,
  action,
  source,
}: CompleteMembershipTransitionParams): Promise<FinalizeMembershipJoinResult> => {
  await performMembershipTransition(action, studyGroupId);

  return finalizeMembershipJoin({
    queryClient,
    studyGroupId,
    source: source ?? defaultSourceByAction[action],
  });
};
